import { memo, useMemo } from "react";
import { formatAddressShort, getSpaceDisplayName } from "../utils";
import { useProposalT } from "../i18n/ProposalI18nContext";
import type { RankedSpace, SpaceAdjustment } from "../../ai-mode/types";
import {
  groupRoomsIntoCombos,
  sumComboTotals,
} from "@/features/vision-360/ai-mode/utils/comboGrouping";

interface SpaceListProps {
  spaces: RankedSpace[];
  spaceAdjustments?: SpaceAdjustment[];
  selectedSpaceId: string;
  onSelectSpace: (id: string) => void;
  hidePrice?: boolean;
}

/**
 * The left-hand list of spaces on the proposal/client page. Rooms that belong to
 * a combo render inside an accented "Conjunto" block; standalone rooms render as
 * plain items. A room that belongs to more than one combo is shown inside each of
 * its combos (replicated). Order is preserved and each combo block is anchored at
 * its first member's position (see groupRoomsIntoCombos).
 */
export const SpaceList = memo(function SpaceList({
  spaces,
  spaceAdjustments = [],
  selectedSpaceId,
  onSelectSpace,
  hidePrice = false,
}: SpaceListProps) {
  const spaceGroups = useMemo(() => groupRoomsIntoCombos(spaces), [spaces]);

  const indexById = useMemo(() => {
    const map = new Map<string, number>();
    spaces.forEach((s, i) => map.set(s.id, i));
    return map;
  }, [spaces]);

  // Per-room client price (what the room shows the client), used to total a combo.
  const clientPriceById = useMemo(() => {
    const map = new Map<string, number>();
    spaces.forEach((s) => {
      const adj = spaceAdjustments.find((a) => a.spaceId === s.id);
      map.set(s.id, adj?.clientPrice ?? s.monthlyPrice ?? 0);
    });
    return map;
  }, [spaces, spaceAdjustments]);

  return (
    <>
      {spaceGroups.map((group) =>
        group.kind === "single" ? (
          <SpaceListItem
            key={group.space.id}
            space={group.space}
            index={indexById.get(group.space.id) ?? 0}
            isSelected={group.space.id === selectedSpaceId}
            onClick={() => onSelectSpace(group.space.id)}
          />
        ) : (
          <ComboBlock
            key={group.comboId}
            comboId={group.comboId}
            spaces={group.spaces}
            {...sumComboTotals(
              group.spaces,
              (s) => clientPriceById.get(s.id) ?? 0,
            )}
            hidePrice={hidePrice}
            selectedSpaceId={selectedSpaceId}
            indexById={indexById}
            onSelectSpace={onSelectSpace}
          />
        ),
      )}
    </>
  );
});

interface ComboBlockProps {
  comboId: string;
  spaces: RankedSpace[];
  totalCapacity: number;
  totalPrice: number;
  hidePrice: boolean;
  selectedSpaceId: string;
  indexById: Map<string, number>;
  onSelectSpace: (id: string) => void;
}

/**
 * "Conjunto" block: the rooms of one combo wrapped in a labeled, accented
 * container so the client sees they belong to the same set. Header shows the
 * aggregate capacity (and total client price, unless prices are hidden). A room
 * shared across combos appears in each combo's block (keyed per combo).
 */
const ComboBlock = memo(function ComboBlock({
  comboId,
  spaces,
  totalCapacity,
  totalPrice,
  hidePrice,
  selectedSpaceId,
  indexById,
  onSelectSpace,
}: ComboBlockProps) {
  const { t } = useProposalT();
  const headerParts = [`${totalCapacity} ${t("spaces.pos")}`];
  if (!hidePrice) {
    headerParts.push(
      `R$ ${totalPrice.toLocaleString("pt-BR")}${t("photos.perMonth")}`,
    );
  }
  return (
    <div
      style={{
        border: "1px solid #C9DAF0",
        background: "#F4F8FD",
        borderRadius: "14px",
        padding: "8px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <p
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          margin: "2px 4px",
          fontSize: "12px",
          fontWeight: 700,
          color: "#3B6FB5",
          letterSpacing: "0.02em",
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
        {t("spaces.combo")} · {headerParts.join(" · ")}
      </p>
      {spaces.map((space) => (
        <SpaceListItem
          key={`${comboId}:${space.id}`}
          space={space}
          index={indexById.get(space.id) ?? 0}
          isSelected={space.id === selectedSpaceId}
          onClick={() => onSelectSpace(space.id)}
        />
      ))}
    </div>
  );
});

interface SpaceListItemProps {
  space: RankedSpace;
  index: number;
  isSelected: boolean;
  onClick: () => void;
}

const SpaceListItem = memo(function SpaceListItem({
  space,
  index,
  isSelected,
  onClick,
}: SpaceListItemProps) {
  const { t } = useProposalT();
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        textAlign: "left",
        padding: "14px 16px",
        borderRadius: "12px",
        border: isSelected ? "2px solid #FFD900" : "1px solid #E8E6E3",
        background: isSelected ? "#FFFDF0" : "white",
        cursor: "pointer",
        transition: "all 0.2s",
      }}
    >
      <div>
        <p
          className="text-sm font-bold"
          style={{ color: "#1A1814", margin: 0 }}
        >
          {getSpaceDisplayName(
            space,
            index,
            t("spaces.option", { number: index + 1 }),
          )}
        </p>
        <p className="text-xs" style={{ color: "#9E9A94", margin: "1px 0 0" }}>
          {formatAddressShort(space.address)}
        </p>
        {space.isRecommended && (
          <span
            style={{
              display: "inline-block",
              marginTop: "4px",
              fontSize: "11px",
              fontWeight: 600,
              color: "#B8860B",
              letterSpacing: "0.03em",
            }}
          >
            {t("spaces.recommended")}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1">
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="#FFD900"
          stroke="none"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
        <span className="text-xs font-semibold" style={{ color: "#1A1814" }}>
          {space.capacity} {t("spaces.pos")}
        </span>
      </div>
    </button>
  );
});
